// src/utils/seoManager.js
// Gestionnaire SEO centralisé — SANS dépendance externe
// Corrigé : champs images (featured_image, logo, cover_image) + support événements

class SEOManager {
  constructor() {
    this.baseUrl      = 'https://www.stagfondation.org'
    this.apiUrl       =  'https://api.stagfondation.org'
    this.siteName     = 'Stag Fondation'
    this.defaultImage = '/img/og-default.png'   // 1200×630 dans public/img/
    this.themeColor   = '#1B3A2D'               // --forest

    this.injectedSchemas = new Set()
  }

  // ═══════════════════════════════════════════════════════════════════
  // MÉTHODE PRINCIPALE — applique tous les meta tags
  // ═══════════════════════════════════════════════════════════════════
  setMetaTags(config) {
    if (!config) return

    const title    = config.title ? `${config.title} | ${this.siteName}` : this.siteName
    const image    = this._resolveImage(config.image)
    const url      = config.url || window.location.href
    const robots   = config.robots  || 'index, follow'
    const ogType   = config.ogType  || 'website'

    // ── Titre ──────────────────────────────────────────────────────
    document.title = title

    // ── Meta de base ───────────────────────────────────────────────
    this._setMeta('description',  config.description || '')
    this._setMeta('keywords',     config.keywords    || '')
    this._setMeta('robots',       robots)
    this._setMeta('author',       this.siteName)
    this._setMeta('theme-color',  this.themeColor)
    this._setMeta('language',     'fr')
    this._setMeta('geo.region',    'CM')
    this._setMeta('geo.placename', 'Douala, Cameroun')

    // ── Canonique ──────────────────────────────────────────────────
    this._setCanonical(url)

    // ── Open Graph ─────────────────────────────────────────────────
    // L'image OG doit être absolue et en 1200×630 pour le meilleur aperçu
    this._setMeta('og:title',        title,               'property')
    this._setMeta('og:description',  config.description   || '', 'property')
    this._setMeta('og:image',        image,               'property')
    this._setMeta('og:image:secure_url', image,           'property')
    this._setMeta('og:image:width',  '1200',              'property')
    this._setMeta('og:image:height', '630',               'property')
    this._setMeta('og:image:alt',    config.title         || this.siteName, 'property')
    this._setMeta('og:url',          url,                 'property')
    this._setMeta('og:type',         ogType,              'property')
    this._setMeta('og:locale',       'fr_FR',             'property')
    this._setMeta('og:site_name',    this.siteName,       'property')

    if (ogType === 'article') {
      if (config.publishedAt) this._setMeta('article:published_time', config.publishedAt, 'property')
      if (config.modifiedAt)  this._setMeta('article:modified_time',  config.modifiedAt,  'property')
      if (config.author)      this._setMeta('article:author',         config.author,       'property')
    }

    // ── Twitter / X Card ───────────────────────────────────────────
    this._setMeta('twitter:card',        'summary_large_image')
    this._setMeta('twitter:site',        '@stagfondation')
    this._setMeta('twitter:creator',     '@stagfondation')
    this._setMeta('twitter:title',       title)
    this._setMeta('twitter:description', config.description || '')
    this._setMeta('twitter:image',       image)
    this._setMeta('twitter:image:alt',   config.title || this.siteName)
  }

  // ═══════════════════════════════════════════════════════════════════
  // GÉNÉRATEURS — pages statiques
  // ═══════════════════════════════════════════════════════════════════

  generateHomeSEO() {
    return {
      title: 'Numérique, Solidarité & Impact au Cameroun',
      description: 'Stag Fondation œuvre pour l\'inclusion numérique : formations IT, accès à Internet dans les quartiers défavorisés, soutien aux associations — Douala, Cameroun.',
      keywords: 'fondation numérique, inclusion digitale, formation IT Cameroun, solidarité, Douala',
      image: '/img/og-home.jpg',
      url: `${this.baseUrl}/`,
      ogType: 'website',
    }
  }

  generateAboutSEO() {
    return {
      title: 'À propos — Mission, valeurs & histoire',
      description: 'Fondée à Oyack source, Douala 3ème, Stag Fondation agit pour l\'éducation des jeunes défavorisés et l\'inclusion numérique au Cameroun depuis 2020.',
      keywords: 'à propos stag fondation, mission, valeurs, inclusion numérique, Douala',
      image: '/img/og-about.jpg',
      url: `${this.baseUrl}/a-propos`,
      ogType: 'website',
    }
  }

  generateContactSEO() {
    return {
      title: 'Contactez-nous',
      description: 'Une question, un partenariat ou un projet ? Contactez l\'équipe Stag Fondation à Douala.',
      keywords: 'contact stag fondation, partenariat, collaboration, Douala',
      image: '/img/og-contact.jpg',
      url: `${this.baseUrl}/contact`,
      ogType: 'website',
    }
  }

  generateTeamSEO() {
    return {
      title: 'Notre équipe',
      description: 'Rencontrez les membres de Stag Fondation : militants de l\'inclusion numérique et experts engagés pour l\'impact social au Cameroun.',
      keywords: 'équipe stag fondation, membres, experts numérique, Douala',
      image: '/img/og-team.jpg',
      url: `${this.baseUrl}/team`,
      ogType: 'website',
    }
  }

  generateDonationSEO() {
    return {
      title: 'Faire un don — Soutenez l\'inclusion numérique',
      description: 'Chaque don finance des formations IT, l\'accès à Internet et le soutien d\'associations à Douala. Agissez maintenant avec Stag Fondation.',
      keywords: 'don stag fondation, faire un don, soutenir, financement numérique',
      image: '/img/og-don.jpg',
      url: `${this.baseUrl}/faire-un-don`,
      ogType: 'website',
    }
  }

  generateBlogSEO() {
    return {
      title: 'Actualités — Blog & Nouvelles',
      description: 'Suivez l\'actualité de Stag Fondation : articles, projets en cours, événements et nouvelles des actions solidaires sur le terrain au Cameroun.',
      keywords: 'actualités stag fondation, blog, nouvelles, événements',
      image: '/img/og-blog.jpg',
      url: `${this.baseUrl}/actualites`,
      ogType: 'website',
    }
  }

  generateProjectsSEO() {
    return {
      title: 'Nos projets',
      description: 'Découvrez les projets de Stag Fondation : déploiement d\'antennes Internet, formations numériques et initiatives de développement communautaire à Douala.',
      keywords: 'projets stag fondation, antennes internet, développement communautaire, Cameroun',
      image: '/img/og-projects.jpg',
      url: `${this.baseUrl}/projets`,
      ogType: 'website',
    }
  }

  generateFormationsSEO() {
    return {
      title: 'Formations IT — Devenez acteur du numérique',
      description: 'Formations en développement web, réseaux et IT pour les jeunes et les professionnels au Cameroun, proposées par Stag Fondation.',
      keywords: 'formations IT, informatique, développement web, numérique, Cameroun',
      image: '/img/og-formations.jpg',
      url: `${this.baseUrl}/formations`,
      ogType: 'website',
    }
  }

  generateAssociationsSEO() {
    return {
      title: 'Associations partenaires',
      description: 'Stag Fondation accompagne des associations qui agissent pour le numérique, l\'éducation et le développement communautaire au Cameroun.',
      keywords: 'associations partenaires, soutien associations, ONG numérique, Cameroun',
      image: '/img/og-associations.jpg',
      url: `${this.baseUrl}/associations`,
      ogType: 'website',
    }
  }

  generateEventsSEO() {
    return {
      title: 'Événements — Tournois, conférences & activités',
      description: 'Découvrez les événements de Stag Fondation : tournois sportifs, conférences, formations et rassemblements communautaires à Douala et au Cameroun.',
      keywords: 'événements stag fondation, tournoi, conférence, activités, Douala, Cameroun',
      image: '/img/og-events.jpg',
      url: `${this.baseUrl}/evenements`,
      ogType: 'website',
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // GÉNÉRATEURS — pages dynamiques
  // ═══════════════════════════════════════════════════════════════════

  /**
   * SEO article (singlenews)
   * Champ image API : featured_image  (ex: "storage/articles/abc.png")
   */
  generateArticleSEO(article) {
    if (!article) return this.generateBlogSEO()

    // Priorité : featured_image (champ réel de l'API) → image → fallback
    const rawImage = article.featured_image || article.image
    const image    = this._resolveApiImage(rawImage)

    return {
      title: article.meta_title || article.title,
      description: this._truncate(
        article.meta_description || article.excerpt || this._stripHtml(article.content), 160
      ),
      keywords: Array.isArray(article.tags)
        ? article.tags.join(', ')
        : (article.tags || 'actualité, fondation, numérique'),
      image,
      url: `${this.baseUrl}/actualite/${article.slug}`,
      ogType: 'article',
      robots: 'index, follow',
      publishedAt: article.published_at,
      modifiedAt:  article.updated_at,
      author:      article.author?.name || this.siteName,
    }
  }

  /**
   * SEO projet (singleproject)
   * Champ image API : featured_image
   */
  generateProjectSEO(project) {
    if (!project) return this.generateProjectsSEO()

    const rawImage = project.featured_image || project.image
    const image    = this._resolveApiImage(rawImage)
    const progress = project.progress != null ? ` Avancement : ${project.progress}%.` : ''

    return {
      title: project.meta_title || project.title,
      description: this._truncate(
        project.meta_description ||
        project.excerpt ||
        `Projet "${project.title}" de Stag Fondation.${progress}`, 160
      ),
      keywords: ['projet solidaire', 'numérique', project.category, 'Stag Fondation']
        .filter(Boolean).join(', '),
      image,
      url: `${this.baseUrl}/projet/${project.slug}`,
      ogType: 'website',
    }
  }

  /**
   * SEO formation (singleformation)
   * Champ image API : featured_image
   */
  generateFormationSEO(formation) {
    if (!formation) return this.generateFormationsSEO()

    const rawImage = formation.featured_image || formation.image
    const image    = this._resolveApiImage(rawImage)
    const spots    = (formation.capacity && formation.enrolled != null)
      ? ` ${formation.capacity - formation.enrolled} place(s) disponible(s).` : ''

    return {
      title: formation.meta_title || formation.title,
      description: this._truncate(
        formation.meta_description ||
        formation.description ||
        `Formation "${formation.title}" par Stag Fondation. Niveau : ${formation.level || 'tous niveaux'}.${spots}`, 160
      ),
      keywords: ['formation IT', 'numérique', formation.level, 'Stag Fondation', 'Cameroun']
        .filter(Boolean).join(', '),
      image,
      url: `${this.baseUrl}/formation/${formation.slug}`,
      ogType: 'website',
    }
  }

  /**
   * SEO association (singleassociation)
   * Champ image API : logo (principal) ou cover_image
   */
  generateAssociationSEO(association) {
    if (!association) return this.generateAssociationsSEO()

    // Pour les associations on prend cover_image d'abord (plus grande), puis logo
    const rawImage = association.cover_image || association.logo || association.logo_url
    const image    = this._resolveApiImage(rawImage)

    return {
      title: association.meta_title || association.name,
      description: this._truncate(
        association.meta_description ||
        association.description ||
        `Découvrez ${association.name}, association partenaire de Stag Fondation.`, 160
      ),
      keywords: ['association', association.category, 'solidarité', 'numérique', 'Cameroun']
        .filter(Boolean).join(', '),
      image,
      url: `${this.baseUrl}/association/${association.slug}`,
      ogType: 'website',
    }
  }

  /**
   * SEO événement (singleevent)
   * Champ image API : featured_image
   */
  generateEventSEO(event) {
    if (!event) return this.generateEventsSEO()

    const rawImage = event.featured_image || event.image
    const image    = this._resolveApiImage(rawImage)

    // Construire une description riche avec les infos clés
    const dateStr = event.start_date
      ? new Date(event.start_date).toLocaleDateString('fr-FR', {
          day: 'numeric', month: 'long', year: 'numeric'
        })
      : ''
    const location = event.is_online ? 'En ligne' : (event.city || event.location || 'Douala')
    const isFree   = (parseFloat(event.registration_fee) || 0) === 0

    const autoDesc = [
      event.description || `Événement Stag Fondation : ${event.title}.`,
      dateStr ? `Le ${dateStr} à ${location}.` : `À ${location}.`,
      isFree ? 'Entrée gratuite.' : `Participation : ${event.registration_fee} ${event.fee_currency}.`,
    ].join(' ')

    return {
      title: event.meta_title || event.title,
      description: this._truncate(
        event.meta_description || event.excerpt || autoDesc, 160
      ),
      keywords: [
        'événement', event.category, 'Stag Fondation', location, 'Cameroun'
      ].filter(Boolean).join(', '),
      image,
      url: `${this.baseUrl}/evenement/${event.slug}`,
      ogType: 'website',
      robots: 'index, follow',
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // JSON-LD — DONNÉES STRUCTURÉES
  // ═══════════════════════════════════════════════════════════════════

  injectOrganizationSchema() {
    this.injectSchema({
      '@context': 'https://schema.org',
      '@type': 'NGO',
      name: this.siteName,
      url: this.baseUrl,
      logo: { '@type': 'ImageObject', url: `${this.baseUrl}/img/logo.png` },
      description: 'Association de la Société Civile œuvrant pour l\'inclusion numérique et l\'éducation des jeunes défavorisés à Douala, Cameroun.',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Douala',
        addressRegion: 'Littoral',
        addressCountry: 'CM',
        streetAddress: 'Oyack source, Douala 3ème'
      },
      sameAs: [
        'https://www.linkedin.com/company/stagfondation',
        'https://twitter.com/stagfondation',
        'https://www.facebook.com/stagfondation'
      ]
    }, 'org-schema')
  }

  injectWebSiteSchema() {
    this.injectSchema({
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: this.siteName,
      url: this.baseUrl,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${this.baseUrl}/actualites?search={search_term_string}`,
        'query-input': 'required name=search_term_string'
      }
    }, 'website-schema')
  }

  injectArticleSchema(article, imageUrl) {
    if (!article) return
    this.injectSchema({
      '@context': 'https://schema.org',
      '@type': 'NewsArticle',
      headline: article.title,
      description: article.excerpt || article.meta_description || '',
      image: imageUrl || `${this.baseUrl}${this.defaultImage}`,
      datePublished: article.published_at,
      dateModified: article.updated_at || article.published_at,
      author: { '@type': 'Person', name: article.author?.name || this.siteName },
      publisher: {
        '@type': 'Organization',
        name: this.siteName,
        logo: { '@type': 'ImageObject', url: `${this.baseUrl}/img/logo.png` }
      },
      mainEntityOfPage: {
        '@type': 'WebPage', '@id': `${this.baseUrl}/actualite/${article.slug}`
      }
    }, 'article-schema')
  }

  injectFormationSchema(formation, imageUrl) {
    if (!formation) return
    this.injectSchema({
      '@context': 'https://schema.org',
      '@type': 'Course',
      name: formation.title,
      description: formation.description || '',
      provider: { '@type': 'Organization', name: this.siteName, url: this.baseUrl },
      url: `${this.baseUrl}/formation/${formation.slug}`,
      ...(imageUrl && { image: imageUrl }),
      ...(formation.level && { educationalLevel: formation.level }),
      ...((formation.price != null) && {
        offers: {
          '@type': 'Offer',
          price: formation.price,
          priceCurrency: 'XAF',
          availability: (formation.enrolled < formation.capacity)
            ? 'https://schema.org/InStock'
            : 'https://schema.org/SoldOut'
        }
      })
    }, 'formation-schema')
  }

  injectProjectSchema(project, imageUrl) {
    if (!project) return
    this.injectSchema({
      '@context': 'https://schema.org',
      '@type': 'Project',
      name: project.title,
      description: project.excerpt || project.description || '',
      url: `${this.baseUrl}/projet/${project.slug}`,
      founder: { '@type': 'Organization', name: this.siteName },
      ...(imageUrl && { image: imageUrl })
    }, 'project-schema')
  }

  injectAssociationSchema(association, imageUrl) {
    if (!association) return
    this.injectSchema({
      '@context': 'https://schema.org',
      '@type': 'NGO',
      name: association.name,
      description: association.description || '',
      url: `${this.baseUrl}/association/${association.slug}`,
      ...(imageUrl && { logo: imageUrl }),
      ...(association.website && { sameAs: [association.website] })
    }, 'association-schema')
  }

  /**
   * Schema JSON-LD pour un événement (Event schema.org)
   */
  injectEventSchema(event, imageUrl) {
    if (!event) return
    const isOnline = event.is_online

    this.injectSchema({
      '@context': 'https://schema.org',
      '@type': 'Event',
      name: event.title,
      description: event.description || event.excerpt || '',
      startDate: event.start_date,
      ...(event.end_date && { endDate: event.end_date }),
      eventStatus: event.status === 'cancelled'
        ? 'https://schema.org/EventCancelled'
        : 'https://schema.org/EventScheduled',
      eventAttendanceMode: isOnline
        ? 'https://schema.org/OnlineEventAttendanceMode'
        : 'https://schema.org/OfflineEventAttendanceMode',
      location: isOnline
        ? { '@type': 'VirtualLocation', url: event.online_url || this.baseUrl }
        : {
            '@type': 'Place',
            name: event.location || event.city || 'Douala',
            address: {
              '@type': 'PostalAddress',
              addressLocality: event.city || 'Douala',
              addressCountry: 'CM'
            }
          },
      organizer: {
        '@type': 'Organization',
        name: this.siteName,
        url: this.baseUrl
      },
      ...(imageUrl && { image: imageUrl }),
      url: `${this.baseUrl}/evenement/${event.slug}`,
      ...((parseFloat(event.registration_fee) > 0) && {
        offers: {
          '@type': 'Offer',
          price: event.registration_fee,
          priceCurrency: event.fee_currency || 'XAF',
          url: `${this.baseUrl}/evenement/${event.slug}`,
          availability: event.registered_count < event.capacity
            ? 'https://schema.org/InStock'
            : 'https://schema.org/SoldOut'
        }
      }),
      ...((parseFloat(event.registration_fee) === 0) && {
        offers: {
          '@type': 'Offer',
          price: 0,
          priceCurrency: 'XAF',
          url: `${this.baseUrl}/evenement/${event.slug}`,
          availability: 'https://schema.org/InStock'
        }
      })
    }, 'event-schema')
  }

  injectBreadcrumbSchema(items) {
    if (!items?.length) return
    this.injectSchema({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: item.url.startsWith('http') ? item.url : `${this.baseUrl}${item.url}`
      }))
    }, 'breadcrumb-schema')
  }

  // ── Gestion schemas ──────────────────────────────────────────────

  injectSchema(schema, id = null) {
    if (!schema) return
    const schemaId = id || `schema-${Date.now()}`
    if (id && this.injectedSchemas.has(id)) this.removeSchema(id)
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.setAttribute('data-schema-id', schemaId)
    script.textContent = JSON.stringify(schema)
    document.head.appendChild(script)
    this.injectedSchemas.add(schemaId)
  }

  removeSchema(id) {
    const el = document.querySelector(`script[data-schema-id="${id}"]`)
    if (el) { el.remove(); this.injectedSchemas.delete(id) }
  }

  clearSchemas() {
    document.querySelectorAll('script[data-schema-id]').forEach(el => {
      this.injectedSchemas.delete(el.getAttribute('data-schema-id'))
      el.remove()
    })
  }

  // ═══════════════════════════════════════════════════════════════════
  // UTILITAIRES PRIVÉS
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Résout le chemin d'une image venant de l'API.
   *
   * L'API stocke les fichiers avec storeFile() qui retourne :
   *   "storage/articles/abc.png"
   *
   * Le frontend construit l'URL complète en concaténant imageUrl + le chemin.
   * imageUrl = "https://api.stagfondation.org/"
   *
   * Cas gérés :
   *   "storage/articles/abc.png"  → "https://api.stagfondation.org/storage/articles/abc.png"
   *   "https://..."               → inchangé
   *   "/img/og.jpg"               → "https://www.stagfondation.org/img/og.jpg"
   *   null / undefined            → image OG par défaut
   */
  _resolveApiImage(path) {
    if (!path) return `${this.baseUrl}${this.defaultImage}`

    // URL absolue → inchangée
    if (path.startsWith('http://') || path.startsWith('https://')) return path

    // Chemin public (statique dans /public)
    // if (path.startsWith('/')) return `${this.baseUrl}${path}`

    // Chemin relatif de l'API (storage/...) → concatène avec l'URL de l'API
    return `${this.apiUrl}/${path}`
  }

  /**
   * Alias pour l'utilisation dans setMetaTags (config.image peut venir de n'importe où)
   */
  _resolveImage(path) {
    return this._resolveApiImage(path)
  }

  _setMeta(name, content, attribute = 'name') {
    if (content === null || content === undefined || content === '') return
    let el = document.querySelector(`meta[${attribute}="${name}"]`)
    if (!el) {
      el = document.createElement('meta')
      el.setAttribute(attribute, name)
      document.head.appendChild(el)
    }
    el.setAttribute('content', String(content))
  }

  _setCanonical(url) {
    if (!url) return
    let el = document.querySelector('link[rel="canonical"]')
    if (!el) {
      el = document.createElement('link')
      el.setAttribute('rel', 'canonical')
      document.head.appendChild(el)
    }
    el.setAttribute('href', url)
  }

  _truncate(text, max = 160) {
    if (!text) return ''
    const clean = text.replace(/\s+/g, ' ').trim()
    return clean.length <= max ? clean : clean.substring(0, max - 3) + '...'
  }

  _stripHtml(html) {
    if (!html) return ''
    return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
  }
}

export default new SEOManager()